import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Crown, PartyPopper, Shield, Eye, Lock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';

const HomePage = () => {
  const { t } = useLanguage();

  const pillars = [
    {
      id: 'nightlife',
      titleKey: 'pillars.nightlife',
      descKey: 'pillars.nightlife.desc',
      image: 'https://images.unsplash.com/photo-1768610284978-8116a501bfe1?auto=format&fit=crop&w=800&q=80',
      icon: Sparkles
    },
    {
      id: 'private',
      titleKey: 'pillars.private',
      descKey: 'pillars.private.desc',
      image: 'https://images.unsplash.com/photo-1601913462945-776e38913868?auto=format&fit=crop&w=800&q=80',
      icon: Crown
    },
    {
      id: 'events',
      titleKey: 'pillars.events',
      descKey: 'pillars.events.desc',
      image: 'https://images.unsplash.com/photo-1763888647863-d6d0c7383151?auto=format&fit=crop&w=800&q=80',
      icon: PartyPopper
    }
  ];

  const experiences = [
    {
      id: 'sp-nightout',
      title: 'São Paulo After Dark',
      subtitle: 'Where insiders gather',
      image: 'https://images.unsplash.com/photo-1583788758158-7a28a8c989ae?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 'rio-weekend',
      title: 'Rio Private Weekend',
      subtitle: 'The city, unveiled',
      image: 'https://images.unsplash.com/photo-1567710726191-c0b9b167239a?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 'villa-chef',
      title: 'Villa & Private Chef',
      subtitle: 'Your world, your rules',
      image: 'https://images.pexels.com/photos/28616663/pexels-photo-28616663.jpeg?auto=compress&w=600'
    },
    {
      id: 'helicopter',
      title: 'Aerial Escapes',
      subtitle: 'Above the ordinary',
      image: 'https://images.unsplash.com/photo-1551636898-47668aa61de2?auto=format&fit=crop&w=600&q=80'
    }
  ];

  const trustPillars = [
    {
      icon: Shield,
      titleKey: 'trust.network',
      descKey: 'trust.network.desc'
    },
    {
      icon: Eye,
      titleKey: 'trust.partners',
      descKey: 'trust.partners.desc'
    },
    {
      icon: Lock,
      titleKey: 'trust.discreet',
      descKey: 'trust.discreet.desc'
    }
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      <SEO />
      
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1722905501021-fe5dd192735c?auto=format&fit=crop&w=1920&q=80"
            alt="São Paulo Skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-obsidian" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="text-gold text-xs md:text-sm uppercase tracking-[0.4em] mb-8 opacity-0 animate-fade-up font-manrope font-medium">
            {t('hero.tagline')}
          </p>
          <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#F5F5F0] mb-8 opacity-0 animate-fade-up animate-delay-100 leading-[1.1]">
            {t('hero.title')}<br />
            <span className="italic text-gold">{t('hero.title.highlight')}</span>
          </h1>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-xl mx-auto mb-12 opacity-0 animate-fade-up animate-delay-200 font-manrope font-light leading-relaxed">
            {t('hero.subtitle')}<br />
            {t('hero.subtitle2')}
          </p>
          <Link
            to="/contact"
            data-testid="hero-cta"
            className="inline-flex items-center gap-3 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold-light transition-all duration-500 opacity-0 animate-fade-up animate-delay-300 luxury-btn"
          >
            {t('hero.cta')}
            <ArrowRight size={16} />
          </Link>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 opacity-0 animate-fade-up animate-delay-500">
          <div className="w-px h-20 bg-gradient-to-b from-gold/60 to-transparent" />
        </div>
      </section>

      {/* Brand Introduction */}
      <section data-testid="brand-intro-section" className="py-28 md:py-36 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-8 font-manrope font-medium">
              {t('intro.tagline')}
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#F5F5F0] mb-10 leading-[1.2]">
              {t('intro.title')}<br />
              <span className="italic text-[#A1A1AA]">{t('intro.title.highlight')}</span>
            </h2>
            <p className="text-[#A1A1AA] text-base md:text-lg leading-[1.9] font-manrope font-light">
              {t('intro.description')}
            </p>
            <div className="flex items-center gap-5 mt-10">
              <div className="w-16 h-px bg-gold/40" />
              <p className="text-gold/80 text-sm font-cormorant italic tracking-wide">
                {t('intro.motto')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section data-testid="pillars-section" className="py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-obsidian-paper">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
              {t('pillars.tagline')}
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0]">
              {t('pillars.title')} <span className="italic">{t('pillars.title.highlight')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {pillars.map((pillar, index) => (
              <div
                key={pillar.id}
                data-testid={`pillar-card-${pillar.id}`}
                className="group relative h-[520px] md:h-[620px] overflow-hidden border border-white/5 hover:border-gold/20 transition-all duration-700"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <img
                  src={pillar.image}
                  alt={t(pillar.titleKey)}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
                <div className="absolute inset-0 flex flex-col justify-end p-8">
                  <pillar.icon className="text-gold mb-5 opacity-70" size={26} strokeWidth={1.5} />
                  <h3 className="font-playfair text-2xl text-[#F5F5F0] mb-3">
                    {t(pillar.titleKey)}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm font-manrope font-light leading-relaxed">
                    {t(pillar.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Layer */}
      <section data-testid="trust-section" className="py-20 md:py-24 px-6 md:px-12 lg:px-24 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {trustPillars.map((item, index) => (
              <div key={index} className="text-center">
                <item.icon className="mx-auto text-gold/60 mb-5" size={28} strokeWidth={1} />
                <h4 className="font-playfair text-lg text-[#F5F5F0] mb-3">{t(item.titleKey)}</h4>
                <p className="text-[#52525B] text-sm font-manrope font-light">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Experiences */}
      <section data-testid="experiences-section" className="py-28 md:py-36 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
                {t('exp.tagline')}
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0]">
                {t('exp.title')} <span className="italic">{t('exp.title.highlight')}</span>
              </h2>
            </div>
            <Link
              to="/experiences"
              data-testid="view-all-experiences"
              className="mt-8 md:mt-0 text-gold text-xs uppercase tracking-[0.2em] font-medium hover:text-gold-light transition-colors inline-flex items-center gap-3 underline-hover"
            >
              {t('exp.viewall')}
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {experiences.map((exp, index) => (
              <Link
                to="/experiences"
                key={exp.id}
                data-testid={`experience-card-${exp.id}`}
                className="group relative h-[420px] overflow-hidden border border-white/5 hover:border-gold/20 transition-all duration-500"
              >
                <img
                  src={exp.image}
                  alt={exp.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <p className="text-gold/80 text-xs uppercase tracking-[0.2em] mb-2 font-manrope">
                    {exp.subtitle}
                  </p>
                  <h3 className="font-playfair text-xl text-[#F5F5F0]">
                    {exp.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section data-testid="cities-section" className="py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-obsidian-paper">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
              {t('cities.tagline')}
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0]">
              {t('cities.title')} <span className="italic">{t('cities.title.highlight')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <Link
              to="/cities"
              data-testid="city-card-sao-paulo"
              className="group relative h-[420px] md:h-[520px] overflow-hidden border border-white/5 hover:border-gold/20 transition-all duration-500"
            >
              <img
                src="https://images.unsplash.com/photo-1583788758158-7a28a8c989ae?auto=format&fit=crop&w=800&q=80"
                alt={t('cities.sp')}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <p className="text-gold/80 text-xs uppercase tracking-[0.2em] mb-3 font-manrope">
                  {t('cities.sp.tagline')}
                </p>
                <h3 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0]">
                  {t('cities.sp')}
                </h3>
              </div>
            </Link>
            
            <Link
              to="/cities"
              data-testid="city-card-rio"
              className="group relative h-[420px] md:h-[520px] overflow-hidden border border-white/5 hover:border-gold/20 transition-all duration-500"
            >
              <img
                src="https://images.unsplash.com/photo-1567710726191-c0b9b167239a?auto=format&fit=crop&w=800&q=80"
                alt={t('cities.rio')}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <p className="text-gold/80 text-xs uppercase tracking-[0.2em] mb-3 font-manrope">
                  {t('cities.rio.tagline')}
                </p>
                <h3 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0]">
                  {t('cities.rio')}
                </h3>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-28 md:py-36 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-gold text-xs uppercase tracking-[0.4em] mb-8 font-manrope font-medium">
            {t('cta.tagline')}
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#F5F5F0] mb-8 leading-[1.2]">
            {t('cta.title')} <span className="italic">{t('cta.title.highlight')}</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-2xl mx-auto mb-12 font-manrope font-light leading-relaxed">
            {t('cta.description')}
          </p>
          <Link
            to="/contact"
            data-testid="cta-button"
            className="inline-flex items-center gap-3 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold-light transition-all duration-500 luxury-btn"
          >
            {t('cta.button')}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
